<template>
  <v-card class="secondary" height="100%">
    <v-form ref="form">
      <v-card-title class="font-weight-medium secondary--text primary">
        {{ edit ? `Edit Discount` : "Add Discount" }}
        <v-spacer></v-spacer>
        <v-btn small icon @click="$emit('closeDialog')">
          <v-icon small color="secondary">mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-card-text class="pt-5">
        <v-row>
          <v-col cols="6">
            <v-text-field
              v-model.trim="form.name"
              label="Name*"
              outlined
              dense
              hide-details="auto"
              :rules="[rules.required]"
              :disabled="loading"
            />
          </v-col>
          <v-col cols="6">
            <v-text-field
              v-model.trim="form.mobile_no"
              label="Mobile Number*"
              outlined
              dense
              maxlength="10"
              hide-details="auto"
              :rules="[rules.required, rules.mobile]"
              :disabled="loading"
            />
          </v-col>
          <v-col cols="6">
            <v-menu
              v-model="showDOBDatePicker"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="290px"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  :value="formattedDob"
                  prepend-inner-icon="mdi-calendar"
                  readonly
                  v-bind="attrs"
                  v-on="on"
                  label="Date of Birth*"
                  outlined
                  dense
                  hide-details="auto"
                  :rules="[rules.required]"
                  :disabled="loading"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="form.dob"
                no-title
                scrollable
                ref="dobPicker"
                :max="sixteenYearsAgo"
                :min="hundredYearsAgo"
                @input="showDOBDatePicker = false"
                :disabled="loading"
              ></v-date-picker>
            </v-menu>
          </v-col>
          <v-col cols="6">
            <v-text-field
              v-model.trim="form.email"
              label="Email*"
              outlined
              dense
              hide-details="auto"
              :rules="[rules.required, rules.email]"
              :disabled="loading"
            />
          </v-col>
          <v-col cols="6">
            <v-select
              v-model="selectedYearBatch"
              :items="yearBatchOptions"
              label="Applicable Batch*"
              outlined
              dense
              hide-details="auto"
              :rules="[rules.required]"
              :disabled="loading"
              clearable
              item-text="label"
              item-value="value"
            />
          </v-col>
          <v-col cols="6">
            <v-combobox
              v-model="form.category"
              :items="categories"
              label="Category*"
              outlined
              dense
              hide-details="auto"
              :rules="[rules.required]"
              :disabled="loading"
              clearable
            />
          </v-col>
          <v-col cols="6">
            <v-text-field
              v-model.number="form.amount"
              label="Amount*"
              outlined
              dense
              hide-details="auto"
              :rules="[rules.required, rules.decimal]"
              :disabled="loading"
            />
          </v-col>
        </v-row>
      </v-card-text>

      <v-card-actions class="pt-0 mb-3 mx-2">
        <v-spacer></v-spacer>
        <v-btn
          class="primary"
          @click="save"
          :loading="loading"
          :disabled="loading"
        >
          {{ edit ? "Update" : "Save" }}
        </v-btn>
      </v-card-actions>
    </v-form>

    <!-- Optional: Full page loader overlay -->
    <v-overlay :value="loading" opacity="0.8" z-index="999">
      <v-progress-circular indeterminate size="64" color="primary">
      </v-progress-circular>
      <span class="mt-3">{{ edit ? "Updating..." : "Creating..." }}</span>
    </v-overlay>
  </v-card>
</template>

<script>
import { rules } from "@/helpers/rules";

export default {
  props: {
    discount: {
      type: Object,
      default: () => ({}),
    },
    edit: {
      type: Boolean,
      default: () => false,
    },
    categories: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      rules,
      loading: false,
      selectedYearBatch: null,
      form: {
        name: "",
        mobile_no: "",
        dob: null,
        email: "",
        category: "",
        amount: null,
        year: null,
        batch: null,
      },
      showDOBDatePicker: false,
    };
  },
  computed: {
    formattedDob() {
      if (!this.form.dob) return "";
      const [year, month, day] = this.form.dob.split("-");
      return `${day}-${month}-${year}`;
    },
    sixteenYearsAgo() {
      const date = new Date();
      date.setFullYear(date.getFullYear() - 16);
      date.setMonth(11);
      date.setDate(31);
      return date.toISOString().substr(0, 10);
    },
    hundredYearsAgo() {
      const date = new Date();
      date.setFullYear(date.getFullYear() - 100);
      date.setMonth(0);
      date.setDate(1);
      return date.toISOString().substr(0, 10);
    },
    yearBatchOptions() {
      const options = [];
      const currentYear = new Date().getFullYear();
      
      // Generate next 5 years
      for (let i = 0; i < 5; i++) {
        const year = currentYear + i;
        
        // Add Batch 1 (Jan-June)
        options.push({
          label: `${year}-1`,
          value: `${year}-1`,
          year: year,
          batch: 1
        });
        
        // Add Batch 2 (July-Dec)
        options.push({
          label: `${year}-2`,
          value: `${year}-2`,
          year: year,
          batch: 2
        });
      }
      
      return options;
    },
  },
  watch: {
    showDOBDatePicker(val) {
      if (val) {
        this.$nextTick(() => {
          if (this.$refs.dobPicker) {
            this.$refs.dobPicker.activePicker = "YEAR";
          }
        });
      }
    },
    selectedYearBatch(newVal) {
      if (newVal) {
        const [year, batch] = newVal.split('-');
        this.form.year = parseInt(year);
        this.form.batch = parseInt(batch);
      } else {
        this.form.year = null;
        this.form.batch = null;
      }
    },
    // Watch for edit mode to set selectedYearBatch from existing data
    'form.year': {
      handler() {
        if (this.form.year && this.form.batch) {
          this.selectedYearBatch = `${this.form.year}-${this.form.batch}`;
        }
      },
      immediate: true
    }
  },
  created() {
    this.init();
  },
  methods: {
    async init() {
      if (this.edit && this.discount && Object.keys(this.discount).length > 0) {
        this.form = { 
          ...this.discount,
          year: this.discount.year || null,
          batch: this.discount.batch || null
        };
        
        if (this.form.year && this.form.batch) {
          this.selectedYearBatch = `${this.form.year}-${this.form.batch}`;
        }
      } else {
        this.form = {
          name: "",
          mobile_no: "",
          dob: null,
          email: "",
          category: "",
          amount: null,
          year: null,
          batch: null,
        };
        this.selectedYearBatch = null;
      }
    },

    async save() {
      if (!this.$refs.form.validate()) return;
      
      if (!this.selectedYearBatch) {
        this.$store.commit("showSnackbar", {
          message: "Please select a batch",
          color: "error",
        });
        return;
      }

      this.loading = true;

      try {
        const submitData = {
          name: this.form.name,
          mobile_no: this.form.mobile_no,
          dob: this.form.dob,
          email: this.form.email,
          category: this.form.category,
          amount: this.form.amount,
          year: this.form.year,
          batch: this.form.batch
        };
        
        if (this.edit) {
          submitData.id = this.form.id;
          submitData.active = this.form.active;
          this.$emit("update", submitData);
        } else {
          submitData.active = true;
          this.$emit("create", submitData);
        }
      } catch (error) {
        console.error("Error in save operation:", error);
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped></style>